<template>
  <div v-if="notification" class="toast-container" :class="notification.type">
    <div class="toast-content">
      <span class="toast-icon">{{ getIcon(notification.type) }}</span>
      <div class="toast-message">
        <p class="toast-title">{{ notification.title }}</p>
        <p v-if="notification.message" class="toast-text">{{ notification.message }}</p>
      </div>
      <button class="toast-close" @click="dismiss">✕</button>
    </div>
    <div class="toast-progress"></div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

const notification = ref(null)
const autoHideTimer = ref(null)

const getIcon = (type) => {
  const icons = {
    'success': '✓',
    'error': '✕',
    'warning': '⚠',
    'info': 'ℹ'
  }
  return icons[type] || '•'
}

const dismiss = () => {
  if (autoHideTimer.value) clearTimeout(autoHideTimer.value)
  notification.value = null
}

const show = (message, type = 'success', title = null, duration = 4000) => {
  notification.value = {
    title: title || getDefaultTitle(type),
    message,
    type
  }

  if (autoHideTimer.value) clearTimeout(autoHideTimer.value)

  autoHideTimer.value = setTimeout(dismiss, duration)
}

const getDefaultTitle = (type) => {
  const titles = {
    'success': 'Succès',
    'error': 'Erreur',
    'warning': 'Attention',
    'info': 'Information'
  }
  return titles[type] || 'Notification'
}

defineExpose({ show, dismiss })
</script>

<style scoped>
.toast-container {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  z-index: 9999;
  min-width: 300px;
  max-width: 400px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  animation: slideInRight 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.toast-container.success {
  background: #f0fdf4;
  border: 1px solid #86efac;
}

.toast-container.error {
  background: #fef2f2;
  border: 1px solid #fca5a5;
}

.toast-container.warning {
  background: #fffbeb;
  border: 1px solid #fcd34d;
}

.toast-container.info {
  background: #f0f9ff;
  border: 1px solid #bae6fd;
}

@keyframes slideInRight {
  from {
    transform: translateX(400px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.toast-content {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  align-items: flex-start;
}

.toast-icon {
  font-size: 1.3rem;
  font-weight: 700;
  flex-shrink: 0;
  margin-top: 0.1rem;
}

.toast-container.success .toast-icon {
  color: #16a34a;
}

.toast-container.error .toast-icon {
  color: #dc2626;
}

.toast-container.warning .toast-icon {
  color: #d97706;
}

.toast-container.info .toast-icon {
  color: #0284c7;
}

.toast-message {
  flex: 1;
}

.toast-title {
  margin: 0;
  font-weight: 600;
  font-size: 0.95rem;
  color: #1f2937;
}

.toast-text {
  margin: 0.3rem 0 0 0;
  font-size: 0.85rem;
  color: #4b5563;
  line-height: 1.4;
}

.toast-close {
  background: none;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0;
  color: #9ca3af;
  flex-shrink: 0;
  transition: color 0.2s;
}

.toast-close:hover {
  color: #6b7280;
}

.toast-progress {
  height: 3px;
  background: linear-gradient(90deg, currentColor, transparent);
  animation: slideProgress 4s linear;
}

.toast-container.success .toast-progress {
  background: linear-gradient(90deg, #16a34a, transparent);
}

.toast-container.error .toast-progress {
  background: linear-gradient(90deg, #dc2626, transparent);
}

.toast-container.warning .toast-progress {
  background: linear-gradient(90deg, #d97706, transparent);
}

.toast-container.info .toast-progress {
  background: linear-gradient(90deg, #0284c7, transparent);
}

@keyframes slideProgress {
  from {
    width: 100%;
  }
  to {
    width: 0%;
  }
}

@media (max-width: 640px) {
  .toast-container {
    bottom: 1rem;
    right: 1rem;
    left: 1rem;
    min-width: auto;
    max-width: none;
  }
}
</style>
